// Route-ready entries for the recPlayback views (frontend plan §4.9).
//
// These wrap the views with device-list loading + URL params so the integrator's
// App.tsx route is a one-liner (see index.ts). They REUSE useDeviceList and
// deviceName, and pick a default cross-day range for View-1.

import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDeviceList } from '../../features/devices/useDeviceList';
import { deviceName } from '../../features/devices/deviceModel';
import { EventTimelineView, type EventTimelineMode } from './EventTimelineView';
import { ContinuousTimelineView } from './ContinuousTimelineView';
import type { DeviceRef, RecRange } from './useRecPlayback';

/** Default View-1 window: last 7 UTC days through now. */
function defaultRange(): RecRange {
  const toUs = Date.now() * 1000;
  return { fromUs: toUs - 7 * 86_400_000_000, toUs };
}

/**
 * View-1 route entry. With :deviceId it shows one device; otherwise all devices
 * (multi-device default, spec §1.2). deviceHlsBase is left undefined here — the
 * integrator supplies a same-LAN base when one is discoverable (Open Q-1).
 */
function EventTimelineRouteInner({ mode }: { mode: EventTimelineMode }) {
  const { t } = useTranslation();
  const { deviceId } = useParams<{ deviceId?: string }>();
  const { devices, loading, error } = useDeviceList();

  const refs: DeviceRef[] = useMemo(() => {
    const all = devices ?? [];
    const picked = deviceId ? all.filter((d) => d.device_id === deviceId) : all;
    return picked.map((d) => ({
      id: d.device_id,
      name: deviceName(d),
      location: d.recording?.location === 'cloud' ? 'cloud' : d.recording?.location === 'sdcard' ? 'sdcard' : undefined,
    }));
  }, [devices, deviceId]);

  const range = useMemo(defaultRange, []);

  if (loading) return <p className="rp-status">{t('recPlayback.loadingDevices')}</p>;
  if (error) return <p className="rp-error">{error}</p>;
  if (refs.length === 0) return <p className="rp-status">{t('recPlayback.noDevices')}</p>;

  return <EventTimelineView devices={refs} range={range} mode={mode} />;
}

/** SD 事件時間軸 — sdcard markers + device-VOD playback. */
export function EventTimelineRoute() {
  return <EventTimelineRouteInner mode="sdcard" />;
}

/** 雲事件時間軸 — cloud DDB events + −19/+24 S3 m3u8 playback. */
export function CloudEventTimelineRoute() {
  return <EventTimelineRouteInner mode="cloud" />;
}

/** View-2 route entry (single device by :deviceId). */
export function ContinuousTimelineRoute() {
  const { t } = useTranslation();
  const { deviceId } = useParams<{ deviceId: string }>();
  const { devices, loading, error } = useDeviceList();

  const device = useMemo(() => {
    const d = (devices ?? []).find((x) => x.device_id === deviceId);
    return d ? { id: d.device_id, name: deviceName(d) } : null;
  }, [devices, deviceId]);

  if (loading) return <p className="rp-status">{t('recPlayback.loadingDevices')}</p>;
  if (error) return <p className="rp-error">{error}</p>;
  if (!device) return <p className="rp-status">{t('recPlayback.noDevices')}</p>;

  return <ContinuousTimelineView device={device} />;
}
